import { FORGOT_PASSWORD_RESET, LINK_FORGOT_PASSWORD } from '@/utils/constants/constants.js';
import alert_mixin from '@/utils/mixins/alert_mixin';
import { validate_password } from '@/utils/validations/validate_inputs';
import { password_visibility_mixin } from '@/utils/mixins/password_visibility_mixin.js';
import axios from 'axios';
import OnboardingIndex from '../OnboardingIndex.vue';

export default {
  name: 'ResetPassword',
  mixins: [alert_mixin , password_visibility_mixin],
  components: {
    OnboardingIndex
  },
  data: () => ({
    new_password: '',
    confirm_password: '',
    user_id: null,
    action_running: false,
    show_password: false,
    link_verified: false,
    password_validation: {
      length: false,
      uppercase: false,
      lowercase: false,
      number: false,
      special: false
    },
    validation_errors: [],
  }),
  async created() {
    // Verify the reset link when component is created
    await this.verify_reset_link();
  },
  watch: {
    new_password: {
      handler(new_value) {
        const result = validate_password(new_value);
        this.password_validation = result.validation;
        this.validation_errors = result.errors;
      },
      immediate: true
    }
  },
  methods: {
    async verify_reset_link() {
      const encrypted_link = this.$route.params.encrypted_link;

      if (!encrypted_link) {
        await this.handle_error('Missing reset link');
        return;
      }

      try {
        const response = await axios.get(LINK_FORGOT_PASSWORD, {
          params: { link: encrypted_link }
        });

        const data = JSON.parse(response.data);

        if (data.status === 'success') {
          this.user_id = data.user_id;
          this.link_verified = true;
        } else {
          await this.handle_error(data.message || 'Invalid reset link');
        }
      } catch (error) {
        const error_message = error.response?.data?.message || 'Invalid or expired reset link';
        await this.handle_error(error_message);
      }
    },

    async reset_password() {
      if (!this.link_verified) {
        await this.show_error('Please wait for link verification');
        return;
      }

      if (this.validation_errors.length > 0) {
        await this.show_error(this.validation_errors[0]);
        return;
      }

      if (this.new_password !== this.confirm_password) {
        await this.show_error('Passwords do not match');
        return;
      }

      this.action_running = true;

      try {
        const response = await axios.post(FORGOT_PASSWORD_RESET, {
          user_id: this.user_id,
          new_password: this.new_password,
          confirm_password: this.confirm_password
        });

        const data = JSON.parse(response.data);
        if (data.status === 'success') {
          await this.show_success('Password reset successfully');
          this.$router.push('/');
        } else {
          await this.show_error(data.message || 'Failed to reset password');
        }
      } catch (error) {
        await this.show_error(error.response?.data?.message || 'Failed to reset password');
      } finally {
        this.action_running = false;
      }
    },
    handle_error(message) {
      this.show_error(message).then(() => {
        this.$router.push('/forgot_password');
      });
    },
    is_password_valid() {
      return Object.values(this.password_validation).every(v => v);
    },
    get_requirement_text: (requirement) => {
      // Define texts object with strict validation
      const texts = Object.freeze({
        length: 'At least 8 characters',
        uppercase: 'One uppercase letter',
        lowercase: 'One lowercase letter',
        number: 'One number',
        special: 'One special character'
      });

      // Validate input is a string and matches expected pattern
      if (typeof requirement !== 'string' || !/^[a-z]+$/.test(requirement)) {
        return 'Invalid requirement';
      }

      // Use Object.prototype.hasOwnProperty for safe property checking
      if (!Object.prototype.hasOwnProperty.call(texts, requirement)) {
        return 'Invalid requirement';
      }

      return texts[`${requirement}`];
    }
  }
};

